/**
 * Голосовое сообщение в TG-стиле.
 *
 * АУДИО ЖИВЁТ В VoicePlayerProvider (route-level), не в этом компоненте —
 * чтобы Virtuoso unmount при скролле не останавливал воспроизведение.
 * Этот компонент только UI: читает состояние из context'а если активный
 * messageId совпадает.
 */
import { useEffect, useRef } from "react";

import { Icon } from "@/components/prototype/Icon";
import { useVoicePlayer } from "@/contexts/VoicePlayerContext";


const BAR_COUNT = 38;


function fmtMmSs(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}


function fakeWaveform(seed: number): number[] {
  let s = seed * 9301 + 49297;
  const out: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    s = (s * 9301 + 49297) % 233280;
    const r = s / 233280;
    const envelope = 0.55 + 0.45 * Math.sin((i / BAR_COUNT) * Math.PI);
    out.push(0.25 + 0.75 * r * envelope);
  }
  return out;
}


function fmtSizeBytes(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}


export function VoiceMessage({
  src, attachmentId, durationSec, waveform, sizeBytes,
  messageId, authorName, ts,
}: {
  src: string;
  attachmentId: number;
  durationSec?: number | null;
  waveform?: number[] | null;
  sizeBytes?: number | null;
  messageId?: number;
  authorName?: string;
  ts?: string;
}) {
  const waveRef = useRef<HTMLDivElement | null>(null);
  const voicePlayer = useVoicePlayer();
  const bars = (waveform && waveform.length > 0) ? waveform : fakeWaveform(attachmentId);

  // Активно ли это голосовое в глобальном плеере?
  const isActive = voicePlayer.meta?.messageId === messageId && messageId != null;
  const playing = isActive && voicePlayer.playing;
  const currentTime = isActive ? voicePlayer.currentTime : 0;
  const duration = isActive ? voicePlayer.duration : (durationSec || 0);

  // Регистрируем play-функцию для prev/next в VoicePlayerBar
  useEffect(() => {
    if (!messageId || !authorName || !ts) return;
    const playFn = () => {
      voicePlayer.play({
        messageId, attachmentId, authorName, ts,
        durationSec: durationSec ?? null,
      }, src);
    };
    return voicePlayer.registerPlayable(messageId, playFn);
  }, [messageId, attachmentId, authorName, ts, durationSec, src, voicePlayer]);

  function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (isActive) {
      voicePlayer.togglePlayPause();
    } else {
      if (!messageId || !authorName || !ts) return;
      voicePlayer.play({
        messageId, attachmentId, authorName, ts,
        durationSec: durationSec ?? null,
      }, src);
    }
  }

  function seekFromPointer(clientX: number) {
    const wave = waveRef.current;
    if (!wave) return;
    // Если voice не активен — стартуем play() сначала, чтобы появился audio
    if (!isActive) {
      if (!messageId || !authorName || !ts) return;
      voicePlayer.play({
        messageId, attachmentId, authorName, ts,
        durationSec: durationSec ?? null,
      }, src);
    }
    const dur = isActive ? duration : (durationSec || 0);
    if (!dur) return;
    const rect = wave.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    voicePlayer.seek(ratio * dur);
  }

  function onWavePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.stopPropagation();
    e.preventDefault();
    seekFromPointer(e.clientX);
    function onMove(ev: PointerEvent) { seekFromPointer(ev.clientX); }
    function onUp() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  const progress = duration > 0 ? currentTime / duration : 0;
  const remaining = Math.max(0, duration - currentTime);
  const timeLabel = playing && currentTime > 0 ? fmtMmSs(remaining) : fmtMmSs(duration);

  return (
    <div className="chat-voice-tg">
      <button
        type="button"
        className="chat-voice-tg-play"
        onClick={toggle}
        title={playing ? "Пауза" : "Воспроизвести"}
      >
        <Icon name={playing ? "pause" : "play"} size={20}/>
      </button>
      <div className="chat-voice-tg-content">
        <div
          className="chat-voice-tg-wave"
          ref={waveRef}
          onPointerDown={onWavePointerDown}
        >
          <div className="chat-voice-tg-wave-layer">
            {bars.map((h, i) => (
              <span key={i} className="chat-voice-tg-bar"
                style={{ height: `${Math.round(h * 100)}%` }}/>
            ))}
          </div>
          <div
            className="chat-voice-tg-wave-layer is-played"
            style={{ clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` }}
          >
            {bars.map((h, i) => (
              <span key={i} className="chat-voice-tg-bar is-played"
                style={{ height: `${Math.round(h * 100)}%` }}/>
            ))}
          </div>
        </div>
        <div className="chat-voice-tg-time">{timeLabel}{sizeBytes ? `, ${fmtSizeBytes(sizeBytes)}` : ""}</div>
      </div>
    </div>
  );
}
